/**
 * The language tabs on the announcement card, worked out without React.
 *
 * Four questions, all of them decisions rather than rendering, which is why
 * they are here and not inside the component: which tabs exist, what a tab is
 * called, whether a tab is finished, and - the one with teeth - exactly what a
 * save is allowed to send.
 *
 * THE PATCH BUILDER IS THE POINT OF THIS FILE. The server merges what it is not
 * sent, so posting all three languages on every save would take whatever this
 * tab last loaded and write it over what a colleague saved in another tab two
 * minutes ago. The page therefore sends only the language the operator touched.
 * That is a rule, it is invisible when it is broken, and it belongs in a
 * function with a test rather than in a JSX callback.
 */
import type {
  NoticeLocale,
  NoticeLocaleOverlay,
  NoticeSlot,
  NoticeVariant,
} from "../../../../lib/calls";

/** The base wording's tab. Not a locale: it is the account's own language. */
export const BASE_TAB = "base" as const;

export type NoticeTab = typeof BASE_TAB | NoticeLocale;

/**
 * The two announcements a translation can be written for.
 *
 * SEVEN of the nine. It was two until the language menu shipped: the other six
 * are reached by an INBOUND caller, and nothing on an inbound call knew what
 * language that caller read, so offering an editable Russian voicemail prompt
 * would have been a form that lies about what it does. The menu asks the caller
 * (docs/crm-telefonia-tegemata.md section 6.12), the answer is pinned on the
 * row, and every inbound announcement is now spoken from it.
 *
 * `menu.language` is the one slot deliberately left out, and it is not an
 * oversight to be tidied up later. It is the sentence that ASKS which language
 * the caller wants, so it has to be understood by somebody who has not chosen
 * one yet - all three languages in a single wording. A per-language tab for it
 * would offer a translation nothing could ever select.
 */
export const TRANSLATABLE_SLOTS: NoticeSlot[] = [
  "notice.outbound.recorded",
  "notice.outbound.processed",
  "notice.inbound.recorded",
  "notice.inbound.processed",
  "inbound.voicemail",
  "inbound.unavailable",
  "hours.voicemail",
  "hours.closed",
  "menu.department",
];

/**
 * The nine announcements, in the order and the runs the tab shows them.
 *
 * ⚠ THE GROUPING IS NOT DECORATION - IT IS THE ONLY THING THAT TELLS NINE
 * IDENTICAL FORMS APART. `TRANSLATABLE_SLOTS` grew from two entries to nine when
 * the language menu shipped, and the tab kept rendering one flat run of blocks
 * that each hold a URL field, a player, a script box and a synthesis row. Nine
 * of those in a column is not a form somebody reads; it is a wall they scroll
 * past. The runs are the four questions an operator actually has - what do we
 * say when we ring you, when you ring us, when nobody picks up, and in the
 * menu - so a person looking for one sentence can skip three quarters of the
 * page without reading it.
 *
 * ⚠ `label` IS A KEY LEAF AND MUST NOT CONTAIN A DOT. `translate()` walks a key
 * by splitting on ".", so a catalogue entry literally named
 * "notice.outbound.recorded" is unreachable - the lookup would descend into a
 * branch called `notice` that does not exist and render the raw key on screen.
 * Slot ids therefore stay slot ids and the label is a separate, flat name.
 *
 * Key leaves rather than wording, because this module is the pure half: the
 * component turns each one into a heading through `t()`. Same split as
 * everywhere else here - this file decides WHAT is shown and in what order,
 * never what it says. `noticeLocales.test.ts` pins the list against
 * `TRANSLATABLE_SLOTS`, so a tenth announcement cannot be added without being
 * placed and named.
 */
export const NOTICE_SLOT_GROUPS: { key: string; slots: { slot: NoticeSlot; label: string }[] }[] = [
  {
    key: "outbound",
    slots: [
      { slot: "notice.outbound.recorded", label: "outboundRecorded" },
      { slot: "notice.outbound.processed", label: "outboundProcessed" },
    ],
  },
  {
    key: "inbound",
    slots: [
      { slot: "notice.inbound.recorded", label: "inboundRecorded" },
      { slot: "notice.inbound.processed", label: "inboundProcessed" },
    ],
  },
  {
    key: "unanswered",
    slots: [
      { slot: "inbound.voicemail", label: "voicemail" },
      { slot: "inbound.unavailable", label: "unavailable" },
      { slot: "hours.voicemail", label: "hoursVoicemail" },
      { slot: "hours.closed", label: "hoursClosed" },
    ],
  },
  {
    key: "menu",
    slots: [{ slot: "menu.department", label: "menuDepartment" }],
  },
];

/** Which languages the tab strip shows, base first. */
export function noticeTabs(locales: NoticeLocale[]): NoticeTab[] {
  return [BASE_TAB, ...locales];
}

/**
 * Has this language been given anything to say?
 *
 * Asked against the slots that are actually editable, not against the whole
 * store: a language whose only entry is one nothing can select would read as
 * finished on the page and change nothing on the phone.
 */
export function localeHasWording(overlay: NoticeLocaleOverlay | undefined): boolean {
  if (!overlay) return false;
  return TRANSLATABLE_SLOTS.some((slot) => {
    const variant = overlay.slots?.[slot];
    return Boolean(variant && (variant.audioUrl.trim() || variant.text.trim()));
  });
}

/**
 * Is this language actually in use?
 *
 * Written and switched on are two different answers, and the tab strip shows
 * them differently on purpose: a paused language keeps its dot, because the
 * work is still there, and gains a struck-through look, because nothing on any
 * call reaches it. Collapsing the two would make "paused" and "empty" look the
 * same, which is exactly the confusion the switch was added to remove.
 *
 * A missing switch reads as ON, the same answer the server's clamp gives.
 */
export function localeInUse(overlay: NoticeLocaleOverlay | undefined): boolean {
  if (!overlay) return false;
  return overlay.enabled !== false && localeHasWording(overlay);
}

/**
 * Would this language really be offered on the dial pad for a call of this
 * shape?
 *
 * The recording switch decides which of the two wordings a call uses, so a
 * translation written only for the recorded sentence does nothing on an account
 * that records nothing. The card says so rather than letting the operator
 * discover it as a picker that never lists their language.
 *
 * The same question `noticeLocalesAvailable` asks on the server. Asked twice on
 * purpose: this one is a sentence on a settings page and that one decides what
 * a caller hears, and a page that had to wait for a round trip to say it would
 * say nothing while the operator was typing.
 */
export function localeReachesCalls(
  overlay: NoticeLocaleOverlay | undefined,
  record: boolean,
): boolean {
  if (overlay?.enabled === false) return false;
  const slot: NoticeSlot = record ? "notice.outbound.recorded" : "notice.outbound.processed";
  const variant = overlay?.slots?.[slot];
  return Boolean(variant && (variant.audioUrl.trim() || variant.text.trim()));
}

/**
 * What one language's slot looks like right now, empty rather than absent.
 *
 * The editor needs a value to render; the store needs the absence to survive
 * until somebody types. Filling in here rather than writing an empty entry to
 * the overlay keeps those two apart.
 */
export function overlayValue(
  overlay: NoticeLocaleOverlay | undefined,
  slot: NoticeSlot,
): NoticeVariant {
  return overlay?.slots?.[slot] ?? { audioUrl: "", text: "" };
}

/**
 * Apply one edit to the in-memory overlays.
 *
 * Copies at every level it touches, because the card holds these in React state
 * and a mutated nested object is an edit that does not repaint.
 */
export function withOverlayEdit(
  locales: Partial<Record<NoticeLocale, NoticeLocaleOverlay>>,
  locale: NoticeLocale,
  slot: NoticeSlot,
  value: NoticeVariant,
): Partial<Record<NoticeLocale, NoticeLocaleOverlay>> {
  const existing = locales[locale];
  return {
    ...locales,
    [locale]: {
      // A language somebody starts writing is on. Editing a PAUSED one leaves
      // it paused: correcting a typo must not put a language somebody
      // deliberately took out of use straight back on the phone.
      enabled: existing?.enabled ?? true,
      slots: { ...(existing?.slots ?? {}), [slot]: value },
    },
  };
}

/** Flip one language in or out of use, without touching a word of it. */
export function withLocaleEnabled(
  locales: Partial<Record<NoticeLocale, NoticeLocaleOverlay>>,
  locale: NoticeLocale,
  enabled: boolean,
): Partial<Record<NoticeLocale, NoticeLocaleOverlay>> {
  const existing = locales[locale];
  return { ...locales, [locale]: { enabled, slots: { ...(existing?.slots ?? {}) } } };
}

/**
 * What the save is allowed to send: the touched languages and nothing else.
 *
 * An empty set means the operator never opened a language tab, and the patch
 * omits `locales` entirely - which the server reads as "this form said nothing
 * about languages" and leaves every translation alone. Sending `{}` instead
 * would be a different claim, and on a merge this deep the difference between
 * "said nothing" and "said there are none" is somebody's afternoon.
 */
export function noticeLocalePatch(
  locales: Partial<Record<NoticeLocale, NoticeLocaleOverlay>>,
  touched: ReadonlySet<NoticeLocale>,
): Partial<Record<NoticeLocale, NoticeLocaleOverlay>> | undefined {
  if (touched.size === 0) return undefined;
  const out: Partial<Record<NoticeLocale, NoticeLocaleOverlay>> = {};
  for (const locale of touched) {
    // A tab opened and cleared is still an answer: it posts an empty overlay,
    // which the server's clamp drops. Skipping it here would leave the old
    // wording in place and make "I deleted it and saved" a lie.
    out[locale] = locales[locale] ?? { enabled: true, slots: {} };
  }
  return out;
}
